import { useCallback } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
type ImageState = {
  contents: Record<
    string,
    {
      id: string;
      url: string;
      loaded: boolean;
      progress?: number;
    }
  >;
  maximized: Record<string, boolean>;
  setMaximized: (id: string, maximized: boolean) => void;
  loadContent(
    id: string,
    downloadFn: (id: string) => Promise<Blob>,
  ): Promise<void>;
  updateContent(
    id: string,
    data: Partial<ImageState['contents'][string]>,
  ): void;
};

export const imagesState = create(
  immer<ImageState>((set, get) => ({
    contents: {},
    maximized: {},
    setMaximized: (id: string, maximized: boolean) => {
      set((state) => {
        state.maximized[id] = maximized;
      });
    },
    loadContent: async (id, downloadFn) => {
      if (get().contents[id]) {
        return;
      }

      get().updateContent(id, {
        loaded: false,
        progress: 0,
      });

      const file: Blob = await downloadFn(id);
      const mediaContent = URL.createObjectURL(file);
      get().updateContent(id, {
        url: mediaContent,
        loaded: true,
        progress: 100,
      });
    },
    updateContent: (id, data) => {
      set((state) => {
        state.contents[id] = {
          ...state.contents[id],
          ...data,
        };
      });
    },
  })),
);

export const useImagesState = imagesState;

export const useImageState = (id: string) => {
  const toggleMaximized = useCallback(() => {
    useImagesState
      .getState()
      .setMaximized(id, !useImagesState.getState().maximized[id]);
  }, [id]);

  const { content, maximized, loadContent } = useImagesState((s) => ({
    content: s.contents[id],
    maximized: s.maximized[id],
    loadContent: s.loadContent,
  }));

  return {
    content,
    maximized,
    toggleMaximized,
    loadContent,
  };
};
